import type { RuleIn } from '../ConfigureDataCollectionAdvancedAdminPanelPage/types'

export const SYSTEM_DISCOVERY_APP_NAME = 'System Discovery'

/**
 * Generates a negative matching criteria rule for System Discovery
 * that excludes all other tracked applications.
 *
 * Takes the original matching_criteria.rule_engine_rule from each app rule
 * and combines them with OR, then wraps in NOT to exclude matches.
 * This preserves the exact matching semantics of each rule (e.g., ALL keywords
 * required) to prevent data loss where System Discovery excludes URLs that
 * no other app rule would actually match.
 */
/**
 * Replaces all context['key'] references in a rule expression with actual values.
 */
function inlineContextValues(ruleExpression: string, context: Record<string, any>): string {
  let inlinedRule = ruleExpression

  // Find all context['key'] or context["key"] references and replace them
  Object.keys(context).forEach((key) => {
    const value = context[key]
    const jsonValue = JSON.stringify(value)

    // Replace both single and double quote variations
    const singleQuotePattern = new RegExp(`context\\['${key}'\\]`, 'g')
    const doubleQuotePattern = new RegExp(`context\\["${key}"\\]`, 'g')

    inlinedRule = inlinedRule.replace(singleQuotePattern, jsonValue)
    inlinedRule = inlinedRule.replace(doubleQuotePattern, jsonValue)
  })

  return inlinedRule
}

export function generateSystemDiscoveryExclusionRule(allRules: RuleIn[]): string {
  const otherAppRules = allRules.filter(
    (r) =>
      r.dashboard_context?.app_name && r.dashboard_context.app_name !== SYSTEM_DISCOVERY_APP_NAME,
  )

  if (otherAppRules.length === 0) {
    return 'true' // No exclusions, track everything
  }

  // Process each rule: inline context values into the rule expression
  const inlinedRuleExpressions = otherAppRules
    .filter((rule) => rule.matching_criteria?.rule_engine_rule)
    .map((rule) => {
      const ruleExpr = rule.matching_criteria.rule_engine_rule
      const context = rule.matching_criteria.context || {}

      // Replace all context['key'] references with actual values
      return inlineContextValues(ruleExpr, context)
    })
    .filter((expr) => expr.trim() !== '')

  if (inlinedRuleExpressions.length === 0) {
    return 'true' // No valid rules to exclude
  }

  // Wrap each inlined rule in parentheses and combine with OR
  const combinedRules = inlinedRuleExpressions.map((expr) => `(${expr})`).join(' or ')

  // Negate the entire expression
  return `not (${combinedRules})`
}

/**
 * Finds the System Discovery rule from all rules.
 */
export function findSystemDiscoveryRule(allRules: RuleIn[]): RuleIn | undefined {
  return allRules.find((r) => r.dashboard_context?.app_name === SYSTEM_DISCOVERY_APP_NAME)
}

/**
 * Updates the System Discovery rule with the new exclusion matching criteria.
 */
export function updateSystemDiscoveryRuleWithExclusions(
  systemDiscoveryRule: RuleIn,
  allRules: RuleIn[],
): RuleIn {
  const exclusionRule = generateSystemDiscoveryExclusionRule(allRules)

  return {
    ...systemDiscoveryRule,
    matching_criteria: {
      ...systemDiscoveryRule.matching_criteria,
      rule_engine_rule: exclusionRule,
    },
  }
}

